/**
 * 司機認證服務
 * 處理 LINE 司機綁定與後續認證
 *
 * 認證流程由 apiClient 統一管理：
 * - 自動 LINE Login（當有 LINE ID Token 但無系統 Token）
 * - 自動 Token Refresh（當 Access Token 過期）
 * - Services 只需調用 API，不需關心認證狀態
 */

import apiClient, {
  ApiBusinessError,
  getStoredAccessToken,
  getStoredRefreshToken,
  clearAuthData,
  lineLogin,
  LineLoginResult,
} from './apiClient'

// ========== Types ==========

export interface LineBindRequest {
  driverId: string
  verificationCode: string
  /** 所屬公司（選填）— 司機編號跨公司重複時由司機選擇後帶入 */
  companyCode?: string
}

export interface LineBindResponse {
  success: boolean
  data: {
    driverId: string
    driverName: string
    companyCode: string
    companyName: string
  }
  accessToken: string
  refreshToken: string
  accessTokenExpiresIn: number
  refreshTokenExpiresIn: number
}

export interface DriverInfoResponse {
  driverId: string
  driverName: string
  companyCode: string
  companyName: string
  status: string
  statusName: string
  hasLineBound: boolean
  currentVehicleNo: string | null
  /** 是否有未交班記錄（工作中） */
  hasUnfinishedDuty: boolean
}

export interface BindDriverResult {
  success: boolean
  message: string
  driver?: {
    driverId: string
    driverName: string
    companyCode: string
    companyName: string
  }
  accessToken?: string
  refreshToken?: string
  accessTokenExpiresIn?: number
  refreshTokenExpiresIn?: number
}

// 向後相容：匯出 token 管理函數和 LINE 登入
export { getStoredAccessToken, getStoredRefreshToken, clearAuthData, lineLogin }
export type { LineLoginResult }

// ========== Service ==========

/**
 * 綁定司機
 *
 * <p>使用系統 JWT（LINE_USER 類型）綁定司機帳號。
 * apiClient 會自動處理認證狀態。
 *
 * @param driverId 司機編號
 * @param verificationCode 驗證碼
 */
export interface BindCompanyOption {
  companyCode: string
  companyName: string
}

/**
 * 綁定預查：該司機編號存在於哪些公司。
 * 司機編號只在同公司內唯一 —— 回傳多家時要讓司機選擇所屬公司再綁定。
 * 預查失敗不阻擋主流程（後端綁定本身也會擋 ambiguous），回空陣列即可。
 */
export async function fetchBindCompanies(driverId: string): Promise<BindCompanyOption[]> {
  try {
    const data = await apiClient.get<BindCompanyOption[]>(
      `/line/drivers/bind/companies?driverId=${encodeURIComponent(driverId)}`
    )
    return data ?? []
  } catch {
    return []
  }
}

export async function bindDriver(
  driverId: string,
  verificationCode: string,
  companyCode?: string
): Promise<BindDriverResult> {
  try {
    // 使用 apiClient 發送請求（會自動處理認證）
    const data = await apiClient.post<LineBindResponse>('/line/drivers/bind', {
      driverId,
      verificationCode,
      ...(companyCode ? { companyCode } : {}),
    } as LineBindRequest)

    if (data && data.success) {
      // 綁定成功後，儲存新的 tokens（DRIVER 類型）
      apiClient.saveAuthTokens(
        data.accessToken,
        data.refreshToken,
        data.accessTokenExpiresIn,
        data.refreshTokenExpiresIn
      )

      return {
        success: true,
        message: '綁定成功',
        driver: data.data,
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
        accessTokenExpiresIn: data.accessTokenExpiresIn,
        refreshTokenExpiresIn: data.refreshTokenExpiresIn,
      }
    }

    return {
      success: false,
      message: '綁定失敗',
    }
  } catch (error) {
    // 處理業務錯誤
    if (error instanceof ApiBusinessError) {
      return {
        success: false,
        message: error.statusDesc || '綁定失敗',
      }
    }

    // 處理 HTTP 錯誤
    if (error && typeof error === 'object' && 'response' in error) {
      const axiosError = error as { response?: { status?: number; data?: { statusDesc?: string; message?: string } } }
      const status = axiosError.response?.status
      const apiMessage = axiosError.response?.data?.statusDesc || axiosError.response?.data?.message

      switch (status) {
        case 401:
          return {
            success: false,
            message: apiMessage || 'LINE 認證已過期，請重新登入',
          }
        case 404:
          return {
            success: false,
            message: apiMessage || '司機編號不存在',
          }
        case 409:
          return {
            success: false,
            message: apiMessage || '此 LINE 帳號已綁定其他司機',
          }
        case 400:
          return {
            success: false,
            message: apiMessage || '驗證碼錯誤',
          }
        default:
          return {
            success: false,
            message: apiMessage || '綁定失敗，請稍後再試',
          }
      }
    }

    return {
      success: false,
      message: '網路錯誤，請檢查連線',
    }
  }
}

/**
 * 取得當前司機資訊
 *
 * <p>apiClient 會自動處理認證狀態。
 */
export async function getMyDriverInfo(): Promise<DriverInfoResponse | null> {
  try {
    // 使用 apiClient 發送請求（會自動處理認證）
    const data = await apiClient.get<DriverInfoResponse>('/line/drivers/me')
    return data
  } catch (error) {
    // 處理 404（LINE 帳號尚未綁定）
    if (error && typeof error === 'object' && 'response' in error) {
      const axiosError = error as { response?: { status?: number } }
      if (axiosError.response?.status === 404) {
        return null
      }
    }

    // 處理業務錯誤
    if (error instanceof ApiBusinessError) {
      console.warn('[DriverAuthService] getMyDriverInfo 失敗:', error.statusDesc)
      return null
    }

    throw error
  }
}
